/** Pull a trailing date phrase off a task's text. The calendar day of `tz` —
 *  the current region's, which the caller passes (C2.1) — throughout: a task
 *  has no hour, so `due_on` is a `YYYY-MM-DD` string, the same convention
 *  `calendar.ts` and `today.ts` use everywhere else.
 *
 *  "Buy milk tomorrow" → { body: 'Buy milk', due_on: <tomorrow> }
 *  "Schedule DEXA scan Friday" → { body: 'Schedule DEXA scan', due_on: <Fri> }
 *
 *  Only a *trailing* phrase counts, which is also what keeps a possessive
 *  from being read as a date (rule 3): "Buy Friday's paper" ends in "paper",
 *  not "Friday", so none of the patterns below match and the body is
 *  untouched.
 *
 *  No imports, the same rule `today.ts` and `yearItems.ts` live by: the root
 *  suite loads this file directly, and Node's ESM resolver will not guess the
 *  extension a bundler would. */

const MONTHS = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
]

const WEEKDAYS = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
]

/** The calendar day a moment falls on in `tz` — `zone.ts`'s `dayIn`, copied
 *  for the reason this file imports nothing. */
function dayIn(at: Date, tz: string): string {
  return at.toLocaleDateString('en-CA', { timeZone: tz })
}

/** A `YYYY-MM-DD` pinned to noon UTC, so arithmetic on it never drifts a day
 *  in either direction — `calendar.ts`'s own `D`, copied for the same reason. */
const D = (s: string) => new Date(`${s}T12:00:00Z`)

function addDaysIso(s: string, n: number): string {
  return new Date(D(s).getTime() + n * 86400000).toISOString().slice(0, 10)
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** What a stripped date phrase leaves behind — a trailing comma, dash or
 *  space — trimmed off so "Buy milk, tomorrow" and "Buy milk tomorrow" both
 *  leave "Buy milk". */
function trimSeparator(s: string): string {
  return s.replace(/[\s,–-]+$/, '').trim()
}

/** The word that introduced the date, left dangling once the date is gone:
 *  "Renew passport by Friday" saved as "Renew passport by" (a3-05). English
 *  and the German Doug also types. Only ever applied after a date phrase has
 *  matched, so "Pay rent on time" keeps its "on"; and only after a space, so
 *  a body that is nothing but the word ("by Friday") keeps it rather than
 *  saving an empty task. */
const DATE_PREPOSITION = /\s+(?:by|on|until|till|before|due|am|bis)$/i

function bodyBefore(s: string): string {
  return trimSeparator(trimSeparator(s).replace(DATE_PREPOSITION, ''))
}

export function dueParse(text: string, now: Date, tz: string): { body: string; due_on: string | null } {
  const trimmed = text.trim()
  const today = dayIn(now, tz)

  // "3 Oct", "3rd October" — a day and a month name, no year: this year,
  // rolled to next if that day has already gone by.
  const dayMonth = trimmed.match(/^(.*?)(?:^|\s)(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3,9})\.?$/)
  if (dayMonth) {
    const mi = MONTHS.indexOf(dayMonth[3].slice(0, 3).toLowerCase())
    if (mi !== -1) {
      const day = Number(dayMonth[2])
      const year = Number(today.slice(0, 4))
      const thisYear = `${year}-${pad2(mi + 1)}-${pad2(day)}`
      const due_on = thisYear >= today ? thisYear : `${year + 1}-${pad2(mi + 1)}-${pad2(day)}`
      return { body: bodyBefore(dayMonth[1]), due_on }
    }
  }

  // "3.10." or "3.10.2027" — day.month, an explicit year rolling nothing.
  const dotted = trimmed.match(/^(.*?)(?:^|\s)(\d{1,2})\.(\d{1,2})(?:\.(\d{4}))?\.?$/)
  if (dotted) {
    const day = Number(dotted[2])
    const month = Number(dotted[3])
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      if (dotted[4]) {
        return { body: bodyBefore(dotted[1]), due_on: `${dotted[4]}-${pad2(month)}-${pad2(day)}` }
      }
      const year = Number(today.slice(0, 4))
      const thisYear = `${year}-${pad2(month)}-${pad2(day)}`
      const due_on = thisYear >= today ? thisYear : `${year + 1}-${pad2(month)}-${pad2(day)}`
      return { body: bodyBefore(dotted[1]), due_on }
    }
  }

  // "tomorrow"
  const tomorrow = trimmed.match(/^(.*?)(?:^|\s)tomorrow\.?$/i)
  if (tomorrow) return { body: bodyBefore(tomorrow[1]), due_on: addDaysIso(today, 1) }

  // "today"
  const todayMatch = trimmed.match(/^(.*?)(?:^|\s)today\.?$/i)
  if (todayMatch) return { body: bodyBefore(todayMatch[1]), due_on: today }

  // "Friday" (the nearest occurrence, today included) or "next Friday" (the
  // one after that — a further week on, not the same Friday restated).
  const weekday = trimmed.match(/^(.*?)(?:^|\s)(next\s+)?([A-Za-z]+)\.?$/i)
  if (weekday) {
    const wi = WEEKDAYS.indexOf(weekday[3].toLowerCase())
    if (wi !== -1) {
      const todayDow = D(today).getUTCDay()
      let ahead = (wi - todayDow + 7) % 7
      if (weekday[2]) ahead += 7
      return { body: bodyBefore(weekday[1]), due_on: addDaysIso(today, ahead) }
    }
  }

  return { body: trimmed, due_on: null }
}
